import { useDropzone } from 'react-dropzone'
import { Upload } from 'lucide-react'
import { getAcceptedFileTypes } from '@/lib/schemas'
import { ARCHIVE_ACCEPT_TYPES } from '@/lib/expand-upload-files'
import { cn } from '@/lib/utils'

interface BidDocumentsDropOverlayProps {
  /**
   * Called with everything dropped — refused types included, so the upload
   * hook can name them as skipped rather than lose them silently.
   */
  onFilesDropped: (files: File[]) => void
  /** When true, the overlay is inert (no drop, no visual). */
  disabled?: boolean
}

/**
 * Wraps its children in a relative container. While the user is dragging
 * files over the container, an absolutely-positioned drop overlay appears.
 * At rest, the overlay takes no space.
 *
 * The dropzone is configured with `noClick` + `noKeyboard` so the only way
 * to open the picker is via the Add → Upload files menu item, which uses
 * the shared upload hook.
 */
export function BidDocumentsDropOverlay({
  onFilesDropped,
  disabled = false,
}: BidDocumentsDropOverlayProps) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles, fileRejections) => {
      const dropped = [...acceptedFiles, ...fileRejections.map(({ file }) => file)]
      if (dropped.length > 0) onFilesDropped(dropped)
    },
    // ZIPs are opened in the browser by the upload hook, never uploaded.
    accept: { ...getAcceptedFileTypes(), ...ARCHIVE_ACCEPT_TYPES },
    multiple: true,
    disabled,
    noClick: true,
    noKeyboard: true,
  })

  return (
    <div
      {...getRootProps()}
      className="absolute inset-0 pointer-events-none"
      data-testid="bid-documents-drop-target"
    >
      <input {...getInputProps()} />
      {/* The overlay receives pointer events only when active, so it doesn't
          interfere with the underlying list at rest. */}
      <div
        className={cn(
          'absolute inset-0 z-30 rounded-lg border-2 border-dashed flex items-center justify-center transition-opacity',
          isDragActive
            ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand))]/5 opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        )}
        aria-hidden={!isDragActive}
      >
        <div className="flex flex-col items-center gap-2 text-center">
          <Upload className="h-8 w-8 text-[hsl(var(--brand))]" />
          <p className="text-sm font-medium">Drop documents to upload</p>
          <p className="text-meta">
            PDF, Word, Excel, PowerPoint, CSV, TXT — or a ZIP or folder
          </p>
        </div>
      </div>
    </div>
  )
}
